import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';
import { AppState } from '../../services/app.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslateHelperService } from '../../services/TranslateService';
import { ConfigService } from '../../services/configService';
import { UserService } from '../../dataServices/userService';
import { ContactInfo, SubdomainConfigService } from 'src/app/services/subdomainConfig.service';

@Component({
  selector: 'app-mobile-navigation',
  templateUrl: './mobile-navigation.component.html',
  styleUrls: ['./mobile-navigation.component.scss'],
  standalone: false
})
export class MobileNavigationComponent implements OnInit, OnChanges {
  @Input() pageTitle: string = '';
  showBackButton: boolean = false;
  menuOpen: boolean = false;
  currentRoute: string = '';
  translatedTitle: string = '';
  showPolicyDropdown: boolean = false;
  policyDropdownOpen: boolean = false;
  quotes: any[] = [];
  activeQuoteIndex: number = 0;
  wcPortalUrl: string = '';
  user: any = null;
  public email: string = '';
  public phone: string = '';
  public claimsReportingUrl: string = '';

  constructor(
    private router: Router,
    private location: Location,
    private appState: AppState,
    private translate: TranslateService,
    private configService: ConfigService,
    private userService: UserService,
    public _translateService: TranslateHelperService,
    private subdomainConfigService: SubdomainConfigService
  ) {
    const contactInfo: ContactInfo = this.subdomainConfigService.getContactInfo();
    this.email = contactInfo.email;
    this.phone = contactInfo.phone;
    this.claimsReportingUrl = contactInfo.claimsReportingUrl;
  }

  ngOnInit(): void {
    // Track route changes to show/hide back button
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.url;
      this.updateBackButtonVisibility();
      this.updatePolicyDropdownVisibility();
    });

    // Initial check
    this.currentRoute = this.router.url;
    this.updateBackButtonVisibility();
    this.updatePolicyDropdownVisibility();

    // Subscribe to quotes from UserService
    this.userService.getQuotes().subscribe(quotes => {
      this.quotes = quotes || [];
      // Set first quote as active by default
      if (this.quotes.length > 0 && this.activeQuoteIndex === 0) {
        this.activeQuoteIndex = 0;
      }
    });

    // Subscribe to user for email subject
    this.appState.user.subscribe(user => {
      this.user = user;
    });

    // Get WC portal URL from config
    this.wcPortalUrl = this.configService.get('wcPortalUrl') || '';

    // Translate the title
    this.translateTitle();
    
    // Re-translate when language changes
    this.translate.onLangChange.subscribe(() => {
      this.translateTitle();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageTitle']) {
      this.translateTitle();
    }
  }

  updateBackButtonVisibility(): void {
    // Show back button on all pages except home and coverage
    const noBackButtonRoutes = ['/home', '/coverage', '/login', '/forgot-password', '/reset-password'];
    this.showBackButton = !noBackButtonRoutes.some(route => this.currentRoute.includes(route));
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  goBack(): void {
    this.location.back();
  }

  logout(): void {
    this.closeMenu();
    this.appState.logOut();
  }

  updatePolicyDropdownVisibility(): void {
    // Show policy dropdown on pages where sidebar is shown
    const showPolicyRoutes = ['/coverage', '/verification-tractors', '/verification-drivers', 
                             '/coverage-proposal', '/verification', '/documents', '/analytics'];
    this.showPolicyDropdown = showPolicyRoutes.some(route => this.currentRoute.includes(route));
  }

  translateTitle(): void {
    if (this.pageTitle) {
      this._translateService.translateAsync(this.pageTitle).subscribe((translated: string) => {
        this.translatedTitle = translated;
      });
    }
  }

  togglePolicyDropdown(): void {
    this.policyDropdownOpen = !this.policyDropdownOpen;
  }

  selectQuote(index: number): void {
    this.activeQuoteIndex = index;
    this.policyDropdownOpen = false;
    // Navigate with query params to update the selected quote
    const selectedQuote = this.quotes[index];
    if (selectedQuote) {
      this.router.navigate([], {
        queryParams: {
          qnum: selectedQuote.qnum,
          qsuffix: selectedQuote.qsuffix
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  getQuoteName(quote: any): string {
    if (quote.quoteName) {
      return quote.quoteName;
    }
    
    let displayName = '';
    
    // Add policy number if available
    if (quote.policyNumber) {
      displayName = `Policy ${quote.policyNumber}`;
    } else if (quote.qnum && quote.qsuffix) {
      displayName = `Quote ${quote.qnum}-${quote.qsuffix}`;
    }
    
    // Add date range if available
    if (quote.effDate && quote.expDate) {
      const startDate = new Date(quote.effDate);
      const endDate = new Date(quote.expDate);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const dateRange = `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()} - ${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`;
        displayName = displayName ? `${displayName} (${dateRange})` : dateRange;
      }
    }
    
    // Fallback if nothing else is available
    return displayName || `Policy ${quote.qnum}-${quote.qsuffix}`;
  }

  activeQuote(index: number): boolean {
    return this.activeQuoteIndex === index;
  }

  getGeneralLiabilityEmailSubject(): string {
    if (this.user?.verificationInfo?.insuredName) {
      return this.user.verificationInfo.insuredName;
    }
    return '';
  }
}